import React, { useMemo } from "react";
import { Loader } from "semantic-ui-react";

import {
    isNonEmptyArray,
    isNonEmptyString,
} from "@cayuse/shared-react.utils.type-checks";

import {
    CAYUSE_TYPES,
    NEW_RULE_TYPES,
} from "globals/constants";
import { useDictionary, usePicklistOptions } from "hooks";

import styles from "./styles.css";

/**
 * These "local hooks" are not useful outside of the builders, any
 * reusable hooks should be put in src/hooks
 *
 *
 */

export const useTeamLabel = (targetId) => {
    const { options: teamOptions, isFetching } = usePicklistOptions("adhocteams");
    const dictionary = useDictionary("ruleBuilder.errors");

    return useMemo(() => {
        if (isFetching) {
            return (
				<span className={styles.loader}>
					<Loader active size="tiny" inline />
				</span>
            );
        }
        const hasTeams = isNonEmptyArray(teamOptions);
        const isValidTargetId = isNonEmptyString(targetId);

        const targetTeam =
            hasTeams &&
            isValidTargetId &&
            teamOptions.find((option) => {
                return option.id === targetId;
            });

        return isNonEmptyString(targetTeam?.name) ? (
            targetTeam.name
        ) : (
            <strong>
                {dictionary.unknownTeam || "Unknown, team cannot be found"}
            </strong>
        );
    }, [teamOptions, targetId, isFetching]);
};

export const findOptionTypes = (obj, accumulator) => {
    if (obj.optionType) {
        accumulator.push(obj.optionType);
    }

    for (const key in obj) {
        if (Array.isArray(obj[key])) {
            obj[key].forEach((item) => {
                findOptionTypes(item, accumulator);
            });
        } else if (typeof obj[key] === "object" && obj[key] !== null) {
            findOptionTypes(obj[key], accumulator);
        }
    }
};

const hasRTRoleSelected = (operand) => {
    return (
        operand.operand.formElementType === CAYUSE_TYPES.RESEARCH_TEAM &&
        operand.operand.optionType === NEW_RULE_TYPES.RT_ROLE
    );
};

const hasUnitAnswered = (operand) => {
    return (
        operand.operand.formElementType === CAYUSE_TYPES.UNIT_FINDER &&
        operand.operand.optionType === NEW_RULE_TYPES.ANSWERED
    );
};

/**
 * @param {{conditions}} - list of operands
 * @description - group operands by AND operator condtion1 and condition2 or condition3 and condition4
 * @returns {{}} array of operands grouped by AND operator [[condtion1, condition2], [condition3, condition4]]
 */
const groupConditions = (conditions) => {
    const groupedConditions = [];
    let andGroup = [];

    conditions.forEach((operand) => {
        if (operand.operator === "OR") {
            groupedConditions.push(andGroup);
            andGroup = [operand];
        } else {
            andGroup.push(operand);
        }
    });

    groupedConditions.push(andGroup);

    return groupedConditions;
};

/**
 * @param {{group}} - condition group
 * @description - validate conditions inside single group
 * @returns {{}} - returns group object with two additional Boolean properties: unitConditionValid and hasUnit
 */
const validateGroup = (group) => {
    group.hasUnit = group.operands.some((operand) => hasUnitAnswered(operand));
    group.hasRTRole = group.operands.some((operand) =>
        hasRTRoleSelected(operand)
    );

    const grouppedOperand = groupConditions(group.operands ?? []);

    if (group.hasUnit) {
        group.unitConditionValid = grouppedOperand.every(
            (groupItems) =>
                groupItems.filter((item) => hasUnitAnswered(item)).length > 0
        );
    }

    if (group.hasRTRole) {
        group.rtRoleConditionValid = grouppedOperand.every(
            (groupItems) =>
                groupItems.filter((item) => hasRTRoleSelected(item)).length > 0
        );
    }

    return group;
};

/**
 * @param {{conditions}} - group conditions
 * @description - validate conditions on group level
 * @returns {{}} - returns whether unit review action should be shown or not
 */
export const compileConditions = (conditions) => {
    (conditions ?? []).forEach((group) => validateGroup(group));

    let showUnitReviewAction = conditions.every(
        (group) => !group.hasUnit || group.unitConditionValid
    );
    let showRTActions = conditions.every(
        (group) => !group.hasRTRole || group.rtRoleConditionValid
    );
    const grouppedOperand = groupConditions(conditions ?? []);

    if (showUnitReviewAction) {
        showUnitReviewAction = grouppedOperand.every(
            (groupItems) => groupItems.filter((item) => item.hasUnit).length > 0
        );
    }

    if (showRTActions) {
        showRTActions = grouppedOperand.every(
            (groupItems) =>
                groupItems.filter((item) => item.hasRTRole).length > 0
        );
    }

    return { showUnitReviewAction, showRTActions };
};
